import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useMatchSocket } from "@/lib/useMatchSocket";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/lib/format";
import { Tv, Volume2, VolumeX } from "lucide-react";
import { enableMatchAudio, disableMatchAudio, playCue } from "@/lib/matchAudio";

export default function SpectatorView() {
  const { matchId: paramId } = useParams();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [selectedId, setSelectedId] = useState(paramId || null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setMatches(data);
        if (!selectedId) {
          const live = data.find((m) => m.status === "live");
          if (live) setSelectedId(live.id);
          else if (data[0]) setSelectedId(data[0].id);
        }
      } catch (e) {
        console.error("[SpectatorView] Failed to load matches", e);
      }
    })();
  }, []);

  if (!selectedId) {
    return (
      <div className="p-10 text-center text-zinc-400" data-testid="spectator-empty">
        <Tv className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
        No matches yet. Create one from the Matches page.
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="px-6 py-3 border-b border-white/10 flex items-center gap-3 flex-wrap" data-testid="spectator-match-picker">
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Court Selector:</span>
        {matches.map((m) => (
          <button
            key={m.id}
            data-testid={`pick-match-${m.id}`}
            onClick={() => { setSelectedId(m.id); nav(`/spectator/${m.id}`); }}
            className={`text-xs px-3 py-1.5 rounded-md border ${selectedId === m.id ? "border-[#00A86B] text-[#00A86B] bg-[#00A86B]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}
          >
            {m.court}: {m.team_a?.name || "A"} vs {m.team_b?.name || "B"}
            {m.status === "live" && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
          </button>
        ))}
      </div>
      <Scoreboard matchId={selectedId} />
    </div>
  );
}

function Scoreboard({ matchId }) {
  const { state: m, event } = useMatchSocket(matchId);
  const [banner, setBanner] = useState(null);
  const [soundOn, setSoundOn] = useState(false);

  useEffect(() => {
    if (!event) return;
    playCue(event.event);
    if (event.event === "golden_point") setBanner({ kind: "golden", team: event.data.team });
    if (event.event === "clash") setBanner({ kind: "clash" });
    if (event.event === "tapout") setBanner({ kind: "tapout", team: event.data.team });
    if (event.event === "shot_clock_violation") setBanner({ kind: "violation", team: event.data.to });
    if (event.event === "lockin_end") setBanner({ kind: "lockin" });
    if (event.event === "subs_locked") setBanner({ kind: "cutoff" });
    const t = setTimeout(() => setBanner(null), 3500);
    return () => clearTimeout(t);
  }, [event]);

  const toggleSound = () => {
    if (soundOn) { disableMatchAudio(); setSoundOn(false); }
    else { setSoundOn(enableMatchAudio()); }
  };

  if (!m) {
    return <div className="flex-1 flex items-center justify-center text-zinc-500" data-testid="spectator-loading">Connecting to live feed…</div>;
  }

  return (
    <div className="flex-1 score-grid-bg flex flex-col" data-testid="spectator-scoreboard">
      {/* Top broadcast bar */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{m.court} · {m.stage?.toUpperCase()}</div>
          <div className="font-anton uppercase text-2xl text-white tracking-tight">Period {m.current_period} / {m.config_snapshot?.num_periods}</div>
        </div>
        <div className="flex items-center gap-4">
          <Button data-testid="spectator-sound-toggle" onClick={toggleSound} variant="outline" size="sm"
            className={`border-white/20 ${soundOn ? "text-[#00A86B]" : "text-zinc-400"}`}>
            {soundOn ? <Volume2 className="w-4 h-4 mr-1.5" /> : <VolumeX className="w-4 h-4 mr-1.5" />}
            {soundOn ? "Sound On" : "Enable Sound"}
          </Button>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Match Clock</div>
            <div data-testid="match-clock" className="font-mono-score text-5xl md:text-6xl text-[#00A86B] leading-none">{fmtTime(m.match_remaining)}</div>
          </div>
        </div>
      </div>

      {/* Banner overlay */}
      {banner && (
        <div className={`px-6 py-4 ${banner.kind === "clash" ? "bg-red-600 clash-shake" : banner.kind === "golden" ? "bg-amber-500 gold-glow" : banner.kind === "violation" ? "bg-red-700" : banner.kind === "cutoff" ? "bg-zinc-700" : "bg-blue-600"} text-black`} data-testid="spectator-banner">
          <div className="font-anton text-3xl md:text-5xl uppercase tracking-tight text-center">
            {banner.kind === "clash" && "⚡ CLASH POINT — RALLY WORTH 4 ⚡"}
            {banner.kind === "golden" && `★ GOLDEN POINT — TEAM ${banner.team.toUpperCase()} ★`}
            {banner.kind === "tapout" && `TAP-OUT — TEAM ${banner.team.toUpperCase()}`}
            {banner.kind === "violation" && `SHOT CLOCK VIOLATION — +1 TEAM ${banner.team.toUpperCase()}`}
            {banner.kind === "lockin" && "DYNAMIC WINDOW OPEN — SUBS ALLOWED"}
            {banner.kind === "cutoff" && "SUBS LOCKED — FINISH WITH PLAYERS ON COURT"}
          </div>
        </div>
      )}

      {/* Score grid */}
      <div className="flex-1 grid md:grid-cols-2 gap-px bg-white/5">
        <TeamPanel side="a" team={m.team_a} score={m.score_a} serving={m.service_team === "a"} goldenPending={m.golden_pending_a} clash={m.clash_pending} />
        <TeamPanel side="b" team={m.team_b} score={m.score_b} serving={m.service_team === "b"} goldenPending={m.golden_pending_b} clash={m.clash_pending} />
      </div>

      {/* Bottom controls */}
      <div className="px-6 py-4 border-t border-white/10 flex flex-wrap items-center gap-4 justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Shot Clock</div>
          <div className={`font-mono-score text-3xl md:text-4xl ${m.shot_clock_remaining < 4 ? "text-red-500" : "text-white"}`} data-testid="shot-clock">
            {fmtTime(m.shot_clock_remaining)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Period</div>
          <div className="font-mono-score text-2xl text-white">{fmtTime(m.period_remaining)}</div>
        </div>
        {m.tapout_active && (
          <div data-testid="tapout-indicator">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400">Tap-Out · Team {m.tapout_team?.toUpperCase()}</div>
            <div className="font-mono-score text-3xl text-amber-400">{fmtTime(m.tapout_remaining)}</div>
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Status</div>
          <div className="font-anton text-xl uppercase text-white">{m.status === "live" ? (m.running ? "RUNNING" : "PAUSED") : m.status}</div>
        </div>
        {m.status === "finished" && m.winner && (
          <div className="px-6 py-2 bg-[#00A86B] text-white font-anton text-2xl uppercase rounded-md" data-testid="match-winner">
            Winner · Team {m.winner.toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamPanel({ side, team, score, serving, goldenPending, clash }) {
  return (
    <div className={`p-8 md:p-12 bg-[#060d09] relative ${clash ? "clash-shake" : ""}`} data-testid={`team-panel-${side}`}>
      <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-2">Team {side.toUpperCase()}</div>
      <div className="font-anton text-3xl md:text-5xl uppercase text-white tracking-tighter mb-6 truncate">
        {team?.name || `Team ${side.toUpperCase()}`}
      </div>
      <div className="flex items-baseline gap-3">
        <div data-testid={`score-${side}`} className="font-anton text-[18vw] md:text-[14vw] leading-[0.85] tracking-tighter text-white">
          {score}
        </div>
        {serving && <div className="text-[10px] uppercase tracking-widest text-[#00A86B]">● Serving</div>}
      </div>
      {(goldenPending || clash) && (
        <div className={`absolute top-4 right-4 px-3 py-1 text-[10px] uppercase tracking-widest font-bold ${clash ? "bg-red-600 text-white clash-shake" : "bg-amber-500 text-black gold-glow"}`}>
          {clash ? "CLASH" : "★ Golden Pending"}
        </div>
      )}
    </div>
  );
}
